import { useState } from 'react'
import { Modal } from '../../components/Common/Modal'
import { Button } from '../../components/Common/Button'

export function UserImport({ onImport, onClose, sidebarCollapsed, isDarkMode = false }) {
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [columnMap, setColumnMap] = useState({})

  // Auto-detect common column name variations
  const AUTO_MAP = {
    'firstname': 'first_name',
    'first name': 'first_name',
    'surname': 'last_name',
    'last name': 'last_name',
    'email address': 'email',
    'email': 'email',
    'user active': 'active',
    'active': 'active',
    'phone': 'mobile',
    'mobile': 'mobile',
    'licence type': 'licence_type',
    'license type': 'licence_type',
    'licence number': 'licence_number',
    'license number': 'licence_number',
    'username': 'advisor_code',
    'user name': 'advisor_code',
    'advisor code': 'advisor_code',
    'code': 'advisor_code',
  }

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return
    setError('')
    setFile(selectedFile)
    setLoading(true)
    try {
      const XLSX = await import('xlsx')
      const reader = new FileReader()
      reader.onload = (event) => {
        try {
          const data = event.target?.result
          const workbook = XLSX.read(data, { type: 'binary' })
          const worksheet = workbook.Sheets[workbook.SheetNames[0]]
          const rows = XLSX.utils.sheet_to_json(worksheet)
          if (rows.length === 0) { setError('No data found in file'); return }

          const headers = Object.keys(rows[0])
          const newMap = {}
          headers.forEach(header => {
            const matched = AUTO_MAP[header.toLowerCase().trim()]
            if (matched) newMap[header] = matched
          })
          setColumnMap(newMap)
          setPreview(rows.slice(0, 5))
        } catch (err) {
          setError(`Error reading file: ${err.message}`)
        }
      }
      reader.readAsBinaryString(selectedFile)
    } catch (err) {
      setError(`Error loading Excel library: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleImport = async () => {
    if (!file || !preview) { setError('No file selected'); return }
    setError('')
    setLoading(true)
    try {
      const XLSX = await import('xlsx')
      const reader = new FileReader()
      reader.onload = async (event) => {
        try {
          const data = event.target?.result
          const workbook = XLSX.read(data, { type: 'binary' })
          const worksheet = workbook.Sheets[workbook.SheetNames[0]]
          const rows = XLSX.utils.sheet_to_json(worksheet)

          const mappedData = rows.map(row => {
            const mapped = {}
            Object.entries(columnMap).forEach(([excelCol, dbCol]) => {
              if (!dbCol || dbCol === 'skip' || row[excelCol] === undefined) return
              if (dbCol === 'active') {
                mapped[dbCol] = row[excelCol] === true || row[excelCol] === 'TRUE' || row[excelCol] === 'Yes' || row[excelCol] === 1
              } else {
                mapped[dbCol] = String(row[excelCol]).trim()
              }
            })
            return mapped
          })

          // Combine first_name + last_name → full_name and name
          const normalized = mappedData
            .map(row => {
              const { first_name, last_name, ...rest } = row
              const full_name = [first_name, last_name].filter(Boolean).join(' ') || ''
              return { ...rest, ...(full_name ? { full_name, name: full_name } : {}) }
            })
            .filter(row => row.email)

          if (normalized.length === 0) {
            setError('No valid rows found. Email Address must be mapped and present on every row.')
            setLoading(false)
            return
          }

          const missingEmail = normalized.filter(row => !row.email)
          if (missingEmail.length > 0) {
            setError(`${missingEmail.length} row(s) are missing an email address and will be skipped. Email is required.`)
          }

          await onImport(normalized)
          setLoading(false)
        } catch (err) {
          setError(`Import error: ${err.message}`)
          setLoading(false)
        }
      }
      reader.onerror = () => { setError('Error reading file'); setLoading(false) }
      reader.readAsBinaryString(file)
    } catch (err) {
      setError(`Error: ${err.message}`)
      setLoading(false)
    }
  }

  const excelColumns = preview ? Object.keys(preview[0]) : []

  return (
    <Modal isOpen={true} onClose={onClose} title="Import Users" sidebarCollapsed={sidebarCollapsed} isDarkMode={isDarkMode}>
      <div className="space-y-4 max-h-[80vh] flex flex-col">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
            {error}
          </div>
        )}

        {!preview ? (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <input
              type="file"
              id="user-file-input"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileSelect}
              disabled={loading}
              className="hidden"
            />
            <label htmlFor="user-file-input" className="cursor-pointer">
              <p className="text-lg font-semibold text-sfp-darkgray mb-2">
                📁 Drop file here or click to select
              </p>
              <p className="text-sm text-gray-600">Supported: Excel (.xlsx, .xls) or CSV</p>
              {loading && <p className="text-sm text-sfp-orange mt-2">Loading...</p>}
            </label>
          </div>
        ) : (
          <div className="space-y-4 flex-1 overflow-y-auto">
            <div>
              <h3 className="font-semibold text-sm text-sfp-darkgray mb-3">
                Map Excel Columns to Database Fields
              </h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {excelColumns.map(col => (
                  <div key={col} className="flex gap-2 items-center text-sm">
                    <span className="flex-1 text-gray-700">{col}</span>
                    <span className="text-gray-400">→</span>
                    <select
                      value={columnMap[col] || ''}
                      onChange={(e) => setColumnMap(prev => ({ ...prev, [col]: e.target.value }))}
                      className="input-field flex-1 text-sm py-1"
                    >
                      <option value="">Skip</option>
                      <option value="first_name">First Name</option>
                      <option value="last_name">Last Name</option>
                      <option value="advisor_code">Advisor Code / Username</option>
                      <option value="email">Email</option>
                      <option value="active">Active</option>
                      <option value="phone">Phone</option>
                      <option value="licence_type">Licence Type</option>
                      <option value="licence_number">Licence Number</option>
                    </select>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-sm text-sfp-darkgray mb-2">
                Preview (first 5 rows)
              </h3>
              <div className="overflow-x-auto border border-gray-200 rounded max-h-40 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="table-header sticky top-0">
                    <tr>
                      {excelColumns.map(col => (
                        <th key={col} className="px-3 py-2 text-left">{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((row, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'table-row-even' : 'table-row-odd'}>
                        {excelColumns.map(col => (
                          <td key={col} className="px-3 py-2 text-gray-700">
                            {String(row[col] ?? '').substring(0, 50)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500 mt-1">Showing first 5 rows</p>
            </div>
          </div>
        )}

        <div className="flex gap-2 justify-end pt-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose} type="button" disabled={loading}>
            Cancel
          </Button>
          {preview && (
            <>
              <Button
                variant="secondary"
                onClick={() => { setPreview(null); setFile(null); setColumnMap({}) }}
                type="button"
                disabled={loading}
              >
                Back
              </Button>
              <Button variant="primary" onClick={handleImport} disabled={loading}>
                {loading ? 'Importing...' : 'Import Users'}
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}
